import React, { useState } from 'react';
import { Card } from './ui/Card';
import { Shield, Info, ChevronDown, ChevronLeft, ChevronRight, ArrowDown, ArrowUp, Filter } from 'lucide-react';
import { AnimatedNumber } from './AnimatedNumber';
import { motion, AnimatePresence } from 'framer-motion';
import { useProjectFilter, AVAILABLE_PROJECTS } from '../contexts/ProjectFilterContext';
interface SiteScore {
  id: string;
  name: string;
  score: number;
  trend: number;
}
const MOCK_SITES: SiteScore[] = [{
  id: '1',
  name: 'Court Square LIC',
  score: 38,
  trend: -4
}, {
  id: '2',
  name: '99 Claremont Ave',
  score: 45,
  trend: -2
}, {
  id: '3',
  name: '251 Douglass St',
  score: 52,
  trend: 1
}, {
  id: '4',
  name: '450 Park Ave S',
  score: 64,
  trend: 3
}, {
  id: '5',
  name: '520 Fifth Avenue',
  score: 71,
  trend: -1
}];
const MOCK_REGIONS: SiteScore[] = [{
  id: '1',
  name: 'New England',
  score: 41,
  trend: -3
}, {
  id: '2',
  name: 'Tri-State',
  score: 57,
  trend: 1
}, {
  id: '3',
  name: 'Mid-Atlantic',
  score: 49,
  trend: -2
}, {
  id: '4',
  name: 'Southeast',
  score: 63,
  trend: 2
}, {
  id: '5',
  name: 'South Florida',
  score: 35,
  trend: -5
}];
const MOCK_TYPES: SiteScore[] = [{
  id: '1',
  name: 'Residential',
  score: 54,
  trend: -2
}, {
  id: '2',
  name: 'Commercial',
  score: 61,
  trend: 1
}, {
  id: '3',
  name: 'Mixed-Use',
  score: 43,
  trend: -3
}, {
  id: '4',
  name: 'Industrial',
  score: 68,
  trend: 4
}, {
  id: '5',
  name: 'Infrastructure',
  score: 39,
  trend: -6
}, {
  id: '6',
  name: 'Healthcare',
  score: 72,
  trend: 2
}];
type TabType = 'sites' | 'regions' | 'type';
const TAB_DATA: Record<TabType, {
  label: string;
  data: SiteScore[];
}> = {
  sites: {
    label: 'Sites',
    data: MOCK_SITES
  },
  regions: {
    label: 'Business Units',
    data: MOCK_REGIONS
  },
  type: {
    label: 'Type',
    data: MOCK_TYPES
  }
};
function getScoreColor(score: number) {
  if (score < 30) return {
    bar: 'bg-red-500',
    text: 'text-red-600'
  };
  if (score < 50) return {
    bar: 'bg-orange-500',
    text: 'text-orange-600'
  };
  if (score < 70) return {
    bar: 'bg-amber-500',
    text: 'text-amber-600'
  };
  return {
    bar: 'bg-emerald-500',
    text: 'text-emerald-600'
  };
}
function getTrendBadge(trend: number) {
  if (trend < 0) {
    return {
      icon: <ArrowDown size={12} data-id="element-3785" />,
      bg: 'bg-red-50',
      text: 'text-red-600',
      border: 'border-red-100'
    };
  }
  if (trend > 0) {
    return {
      icon: <ArrowUp size={12} data-id="element-3786" />,
      bg: 'bg-emerald-50',
      text: 'text-emerald-600',
      border: 'border-emerald-100'
    };
  }
  return {
    icon: null,
    bg: 'bg-gray-50',
    text: 'text-gray-500',
    border: 'border-gray-100'
  };
}
export function SafetyScoreCard() {
  const [activeTab, setActiveTab] = useState<TabType>('sites');
  const [sortAscending, setSortAscending] = useState(true);
  const {
    selectedProjects
  } = useProjectFilter();
  const allProjectsSelected = selectedProjects.length === AVAILABLE_PROJECTS.length;
  const isFiltered = !allProjectsSelected;
  const overallScore = 57;
  const overallTrend = -1;
  const trendBadge = getTrendBadge(overallTrend);
  const scoreColor = getScoreColor(overallScore);
  const currentData = TAB_DATA[activeTab].data;
  const sortedData = [...currentData].sort((a, b) => sortAscending ? a.score - b.score : b.score - a.score);
  return <Card className="h-full flex flex-col" data-id="element-3787">
      {/* Header */}
      <div className="flex justify-between items-start mb-4" data-id="element-3788">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-3789">
          <div data-id="element-3790">
            <div className="flex items-center gap-2" data-id="element-3791">
              <h3 className="font-bold text-gray-900 text-sm" data-id="element-3792">
                Compliance Score
              </h3>
              <button className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3793">
                <Info size={14} data-id="element-3794" />
              </button>
            </div>
            <p className="text-xs text-gray-500" data-id="element-3795">Weekly safety performance</p>
          </div>
          {isFiltered && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-3796">
              <Filter size={10} data-id="element-3797" />
              Filtered
            </motion.div>}
        </div>
        <button className="text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors" data-id="element-3798">
          View all →
        </button>
      </div>

      {/* Overall Score + Trend */}
      <div className="flex items-center gap-4 mb-4" data-id="element-3799">
        <div className="flex items-baseline gap-1.5" data-id="element-3800">
          <span className="text-3xl font-bold text-gray-900" data-id="element-3801">
            <AnimatedNumber value={overallScore} data-id="element-3802" />
          </span>
          <span className="text-sm text-gray-400" data-id="element-3803">/100</span>
        </div>

        <div className={`flex items-center gap-1 ${trendBadge.bg} ${trendBadge.text} border ${trendBadge.border} px-2 py-1 rounded-md text-xs font-semibold`} data-id="element-3804">
          {trendBadge.icon}
          <span data-id="element-3805">{Math.abs(overallTrend)}</span>
          <span className="font-normal opacity-75" data-id="element-3806">vs last wk</span>
        </div>

        {/* Score bar */}
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3807">
          <motion.div initial={{
          width: 0
        }} animate={{
          width: `${overallScore}%`
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className={`h-full ${scoreColor.bar} rounded-full`} data-id="element-3808" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3" data-id="element-3809">
        {(['sites', 'regions', 'type'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeTab === tab ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3810">
            {TAB_DATA[tab].label} ({TAB_DATA[tab].data.length})
          </button>)}
      </div>

      {/* Sort control */}
      <div className="flex items-center justify-between mb-2" data-id="element-3811">
        <span className="text-[11px] text-gray-400 uppercase tracking-wide font-medium" data-id="element-3812">
          {TAB_DATA[activeTab].label}
        </span>
        <button onClick={() => setSortAscending(!sortAscending)} className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700 transition-colors" data-id="element-3813">
          Score: {sortAscending ? 'Low → High' : 'High → Low'}
          <ChevronDown size={12} className={`transition-transform ${sortAscending ? '' : 'rotate-180'}`} data-id="element-3814" />
        </button>
      </div>

      {/* Items List */}
      <div className="flex-1 space-y-2" data-id="element-3815">
        <AnimatePresence mode="popLayout" data-id="element-3816">
          {sortedData.map((item, index) => {
          const itemColor = getScoreColor(item.score);
          const itemTrend = getTrendBadge(item.trend);
          return <motion.div key={item.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            delay: index * 0.04
          }} className="flex items-center gap-3 group" data-id="element-3817">
                <div className="w-[100px] text-xs font-medium text-gray-800 truncate" title={item.name} data-id="element-3818">
                  {item.name}
                </div>
                <div className="flex-1 flex items-center gap-2" data-id="element-3819">
                  <span className={`text-sm font-bold w-7 text-right ${itemColor.text}`} data-id="element-3820">
                    <AnimatedNumber value={item.score} data-id="element-3821" />
                  </span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3822">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${item.score}%`
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.05,
                  ease: 'easeOut'
                }} className={`h-full ${itemColor.bar} rounded-full`} data-id="element-3823" />
                  </div>
                </div>
                <div className={`flex items-center gap-0.5 ${itemTrend.text} text-[11px] font-semibold min-w-[32px] justify-end`} data-id="element-3824">
                  {itemTrend.icon}
                  <span data-id="element-3825">{Math.abs(item.trend)}</span>
                </div>
              </motion.div>;
        })}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100" data-id="element-3826">
        <span className="text-xs text-gray-400" data-id="element-3827">
          Updated{' '}
          {new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })}
        </span>
        <span className="text-[11px] text-gray-400" data-id="element-3828">
          {sortedData.length} of {currentData.length}
        </span>
      </div>
    </Card>;
}